import { Suspense } from 'react';
import { Outlet, Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import OrbitBackdrop from '@/components/OrbitBackdrop';
import BrandLogo from '@/components/BrandLogo';
import PageFallback from '@/components/PageFallback';

/**
 * Centred authentication shell, using the marketing site's hero treatment:
 * a pale lavender field, concentric rings with social chips orbiting a soft
 * cyan-into-lavender glow, and the form card floating at the centre.
 *
 * Replaces the old half-brand / half-form split, which squeezed the form into a
 * narrow column off to one side at laptop widths.
 */
export default function AuthLayout() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        py: { xs: 5, sm: 7 },
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #EDEFF6 0%, #E8EAF4 55%, #EFF0F7 100%)',
      }}
    >
      <OrbitBackdrop />

      {/* Brand */}
      <Box
        component={RouterLink}
        to="/login"
        sx={{ mb: 3, textDecoration: 'none', position: 'relative', zIndex: 1, display: 'inline-flex' }}
      >
        <BrandLogo height={40} />
      </Box>

      {/* The card sits above the orbit, with a soft ring so it reads as
          floating over the rings rather than punched out of them. */}
      <Box
        sx={{
          width: '100%', maxWidth: 428, position: 'relative', zIndex: 1,
          bgcolor: 'rgba(255,255,255,.92)',
          backdropFilter: 'blur(14px)',
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          border: '1px solid rgba(255,255,255,.9)',
          boxShadow: '0 28px 64px -22px rgba(30,42,94,.35), 0 2px 8px rgba(30,42,94,.06)',
        }}
      >
        {/* Its own loading boundary, so moving between sign-in screens keeps
            the backdrop and the card instead of blanking the whole page. */}
        <Suspense fallback={<PageFallback minHeight={240} />}>
          <Outlet />
        </Suspense>
      </Box>

      <Typography
        variant="caption"
        sx={{ mt: 3, color: '#6E769B', position: 'relative', zIndex: 1, textAlign: 'center' }}
      >
        © {new Date().getFullYear()} Publifying — every network you&apos;re on, one workflow.
      </Typography>
    </Box>
  );
}
